import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Frame-rate independent gamepad input hook
 * Works consistently across all refresh rates (60Hz, 144Hz, 165Hz, 240Hz, etc.)
 *
 * @param {Object} config - Configuration object
 * @param {Function} config.onNavigate - Callback for D-pad/analog navigation: (direction) => void
 *   direction: 'left' | 'right' | 'up' | 'down'
 * @param {Function} config.onButtonPress - Callback for button presses: (button) => void
 *   button: 'A' | 'B' | 'X' | 'Y' | 'LB' | 'RB' | 'LT' | 'RT' | 'select' | 'start'
 * @param {Function} config.onRawInput - Callback for raw button/direction input: ({ type, buttonIndex, direction }) => void
 *   type: 'button' | 'navigation'
 *   buttonIndex: 0-15 for buttons
 *   direction: 'left' | 'right' | 'up' | 'down' for navigation
 * @param {number} config.navigationDelay - Delay between navigation inputs in ms (default: 150)
 * @param {number} config.buttonDelay - Delay between button presses in ms (default: 150)
 * @param {number} config.repeatDelay - Delay for raw input repeat (default: 150)
 * @param {boolean} config.enabled - Whether gamepad input is active (default: true)
 * @param {number} config.deadzone - Analog stick deadzone (default: 0.5)
 * @param {number} config.gamepadIndex - Which gamepad to use: 0 or 1 (default: null = any)
 *
 * @example
 * // Traditional usage with named callbacks
 * useGamepad({
 *   onNavigate: (direction) => {
 *     if (direction === 'left') moveLeft();
 *     if (direction === 'right') moveRight();
 *   },
 *   onButtonPress: (button) => {
 *     if (button === 'A') selectItem();
 *     if (button === 'B') goBack();
 *   },
 *   navigationDelay: 150,
 *   buttonDelay: 150,
 *   enabled: true
 * });
 *
 * @example
 * // Raw input with custom bindings
 * useGamepad({
 *   onRawInput: ({ type, buttonIndex, direction }) => {
 *     if (type === 'button') {
 *       const action = customBindings[buttonIndex];
 *       handleAction(action);
 *     } else if (type === 'navigation') {
 *       handleNavigation(direction);
 *     }
 *   },
 *   repeatDelay: 150,
 *   gamepadIndex: 0
 * });
 */
export const useGamepad = ({
  onNavigate,
  onButtonPress,
  onRawInput,
  navigationDelay = 150,
  buttonDelay = 150,
  repeatDelay = 150,
  enabled = true,
  deadzone = 0.5,
  gamepadIndex = null
}) => {
  const animationFrameRef = useRef(null);
  const lastInputTimeRef = useRef({
    navigation: 0,
    A: 0,
    B: 0,
    X: 0,
    Y: 0,
    LB: 0,
    RB: 0,
    LT: 0,
    RT: 0,
    select: 0,
    start: 0
  });
  const lastRawInputTimeRef = useRef(0);
  const previousButtonRef = useRef(null);

  const gamepadLoop = useCallback(() => {
    if (!enabled) {
      animationFrameRef.current = requestAnimationFrame(gamepadLoop);
      return;
    }

    const gamepads = navigator.getGamepads();
    let gamepad;

    // Get the specified gamepad or find any connected one
    if (gamepadIndex !== null) {
      gamepad = gamepads[gamepadIndex];
    } else {
      gamepad = gamepads[0] || gamepads[1] || gamepads[2] || gamepads[3];
    }

    if (!gamepad) {
      animationFrameRef.current = requestAnimationFrame(gamepadLoop);
      return;
    }

    // Use performance.now() for high-resolution, frame-rate independent timing
    const now = performance.now();
    const axes = gamepad.axes;
    const buttons = gamepad.buttons;

    // Handle raw input (for custom bindings)
    if (onRawInput) {
      // Find currently pressed button
      let currentPressedButtonIndex = null;
      for (let i = 0; i < buttons.length; i++) {
        if (buttons[i].pressed) {
          currentPressedButtonIndex = i;
          break;
        }
      }

      // Check for button change or repeat timing
      if (currentPressedButtonIndex !== previousButtonRef.current) {
        if (currentPressedButtonIndex !== null) {
          onRawInput({ type: 'button', buttonIndex: currentPressedButtonIndex });
          lastRawInputTimeRef.current = now;
        }
        previousButtonRef.current = currentPressedButtonIndex;
      } else if (currentPressedButtonIndex !== null && now - lastRawInputTimeRef.current >= repeatDelay) {
        // Button held - repeat action
        onRawInput({ type: 'button', buttonIndex: currentPressedButtonIndex });
        lastRawInputTimeRef.current = now;
      }

      // Check for navigation (D-pad or analog stick)
      if (now - lastInputTimeRef.current.navigation >= repeatDelay) {
        const moveLeft = axes[0] < -deadzone || buttons[14]?.pressed;
        const moveRight = axes[0] > deadzone || buttons[15]?.pressed;
        const moveUp = axes[1] < -deadzone || buttons[12]?.pressed;
        const moveDown = axes[1] > deadzone || buttons[13]?.pressed;

        if (moveLeft) {
          onRawInput({ type: 'navigation', direction: 'left' });
          lastInputTimeRef.current.navigation = now;
        } else if (moveRight) {
          onRawInput({ type: 'navigation', direction: 'right' });
          lastInputTimeRef.current.navigation = now;
        } else if (moveUp) {
          onRawInput({ type: 'navigation', direction: 'up' });
          lastInputTimeRef.current.navigation = now;
        } else if (moveDown) {
          onRawInput({ type: 'navigation', direction: 'down' });
          lastInputTimeRef.current.navigation = now;
        }
      }
    }

    // Handle D-pad or Left Stick navigation (backward compatibility)
    if (onNavigate && now - lastInputTimeRef.current.navigation >= navigationDelay) {
      const moveLeft = axes[0] < -deadzone || buttons[14]?.pressed;
      const moveRight = axes[0] > deadzone || buttons[15]?.pressed;
      const moveUp = axes[1] < -deadzone || buttons[12]?.pressed;
      const moveDown = axes[1] > deadzone || buttons[13]?.pressed;

      if (moveLeft) {
        lastInputTimeRef.current.navigation = now;
        onNavigate('left');
      } else if (moveRight) {
        lastInputTimeRef.current.navigation = now;
        onNavigate('right');
      } else if (moveUp) {
        lastInputTimeRef.current.navigation = now;
        onNavigate('up');
      } else if (moveDown) {
        lastInputTimeRef.current.navigation = now;
        onNavigate('down');
      }
    }

    // Handle button presses (backward compatibility)
    if (onButtonPress) {
      const buttonMap = {
        A: 0,
        B: 1,
        X: 2,
        Y: 3,
        LB: 4,
        RB: 5,
        LT: 6,
        RT: 7,
        select: 8,
        start: 9
      };

      Object.entries(buttonMap).forEach(([buttonName, buttonIndex]) => {
        if (buttons[buttonIndex]?.pressed && now - lastInputTimeRef.current[buttonName] >= buttonDelay) {
          lastInputTimeRef.current[buttonName] = now;
          onButtonPress(buttonName);
        }
      });
    }

    // Continue the loop at monitor's refresh rate
    animationFrameRef.current = requestAnimationFrame(gamepadLoop);
  }, [enabled, onNavigate, onButtonPress, onRawInput, navigationDelay, buttonDelay, repeatDelay, deadzone, gamepadIndex]);

  useEffect(() => {
    // Start the animation frame loop
    animationFrameRef.current = requestAnimationFrame(gamepadLoop);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [gamepadLoop]);
};

/**
 * Hook to detect if any gamepad is connected
 * @returns {boolean} - Whether a gamepad is connected
 */
export const useGamepadConnected = () => {
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const checkGamepad = () => {
      const gamepads = navigator.getGamepads();
      const hasGamepad = Array.from(gamepads).some(gp => gp !== null);
      setIsConnected(hasGamepad);
    };

    checkGamepad();
    window.addEventListener('gamepadconnected', checkGamepad);
    window.addEventListener('gamepaddisconnected', checkGamepad);

    // Check periodically in case events don't fire
    const interval = setInterval(checkGamepad, 2000);

    return () => {
      window.removeEventListener('gamepadconnected', checkGamepad);
      window.removeEventListener('gamepaddisconnected', checkGamepad);
      clearInterval(interval);
    };
  }, []);

  return isConnected;
};
